import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { HOME } from "@/constants/testIds";
import {
  ArrowRight, ArrowDown, Calendar, Users, MapPin, BellRing, FileText, Mic2,
  Briefcase, Sparkles, CloudSun, Quote, Star, Handshake,
  Hammer, Building2, Shield, Cpu, Megaphone, UserCheck,
  ClipboardList, Presentation, CalendarClock, Trophy,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import CountdownTo from "@/components/CountdownTo";
import { CountUp, FadeUp } from "@/components/Motion";

// Cinematic backdrop — actual Travel + Leisure HQ still, 501 W. Church Street, Orlando.
const HERO_STILL_A = "/brand/tl-hq.jpeg";
const HERO_VIDEO_SRC = null;

const QUICK_ACCESS = [
  { to: "/register",  label: "Register",           icon: FileText,  testId: HOME.registerBtn },
  { to: "/schedule",  label: "Schedule",           icon: Calendar,  testId: HOME.scheduleBtn },
  { to: "/directory", label: "Supplier Directory", icon: Users,     testId: HOME.directoryBtn },
  { to: "/venue",     label: "Showcase",           icon: MapPin,    testId: "home-venue-btn" },
  { to: "/ask",       label: "Summit Concierge",   icon: Sparkles,  testId: "home-concierge-btn" },
];

const WHY_ATTEND = [
  { icon: Hammer,  name: "Construction",              body: "Capital projects, buildouts, and resort renovations." },
  { icon: Building2, name: "Resort Operations",       body: "Day-to-day guest experience & operational excellence." },
  { icon: Shield,  name: "Security / Risk Management", body: "Enterprise security, compliance, and risk programs." },
  { icon: Cpu,     name: "Information Technology",     body: "Guest platforms, cloud, data, and infrastructure." },
  { icon: Megaphone, name: "Marketing",                body: "Brand, creative, media, and loyalty activation." },
  { icon: UserCheck, name: "Staffing",                 body: "Talent partners across Travel + Leisure operations." },
];

const JOURNEY_STEPS = [
  { icon: FileText,      label: "Register" },
  { icon: ClipboardList, label: "Complete Supplier Profile" },
  { icon: MapPin,        label: "Select Supplier Station" },
  { icon: CalendarClock, label: "Schedule Business Matchmaking" },
  { icon: Presentation,  label: "Attend Summit" },
  { icon: Trophy,        label: "Post-Event Business Connections" },
];

export default function HomePage() {
  const [event, setEvent] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [weather, setWeather] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const exploreRef = useRef(null);

  useEffect(() => {
    api.get("/event").then((r) => setEvent(r.data)).catch(() => {});
    api.get("/executive-updates").then((r) => setUpdates(r.data.updates || [])).catch(() => {});
    // Speakers roster intentionally not fetched on public Home — feature-flagged off
    // until Display America confirms the final lineup.
    api.get("/exhibitors").then((r) => setExhibitors(r.data.exhibitors || [])).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data.sponsors || [])).catch(() => {});
    api.get("/weather").then((r) => setWeather(r.data)).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {});
    api.get("/event/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Featured Prime Supplier: admin-selected OR first approved+paid (rotate through)
  const featuredSupplier = useMemo(() => {
    const eligible = exhibitors.filter((e) => e.status === "approved" && e.payment_status === "paid" && !/^TEST[_ ]/i.test(e.company_name || ""));
    if (event?.featured_supplier_id) return eligible.find((e) => e.id === event.featured_supplier_id) || null;
    if (eligible.length === 0) return null;
    // Rotate deterministically by day-of-year so home stays fresh
    const doy = Math.floor((Date.now() / 86400000) % eligible.length);
    return eligible[doy] || eligible[0];
  }, [event, exhibitors]);

  // Summit Dashboard metrics (dynamic)
  const dashboard = [
    { value: stats?.total_companies ?? exhibitors.length, label: "Prime Suppliers Registered", testId: "kpi-prime" },
    { value: stats?.tl_leaders ?? 20, label: "Travel + Leisure Leaders", testId: "kpi-tl" },
    { value: stats?.meeting_volume ?? 0, label: "Matchmaking Meetings", testId: "kpi-meetings" },
    { value: stats?.sbes_invited ?? 30, label: "SBEs Invited", testId: "kpi-sbes" },
  ];

  // Today's Highlights: dynamically pull from sessions
  const todaysHighlights = useMemo(() => {
    if (!sessions.length) return [];
    const opening = sessions.find((s) => s.kind === "opening");
    const forum = sessions.find((s) => s.kind === "fireside");
    const lunch = sessions.find((s) => s.kind === "meal");
    const showcase = sessions.find((s) => s.kind === "showcase" || s.kind === "networking");
    return [opening, forum, lunch, showcase]
      .filter(Boolean)
      .map((s) => {
        // Custom labels for premium executive framing
        if (s.kind === "fireside") return { ...s, displayTitle: s.title || "Sourcing Fireside Chat", displaySubtitle: null };
        return { ...s, displayTitle: s.title, displaySubtitle: null };
      });
  }, [sessions]);

  const host = sponsors.find((s) => s.tier === "host");
  const producedBy = sponsors.find((s) => s.tier === "produced_by");
  const techPartner = sponsors.find((s) => s.tier === "technology_partner");
  const supporting = sponsors.filter((s) => s.tier === "supporting");

  const topUpdate = updates[0];

  const scrollToExplore = () => {
    exploreRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Layout eventName={event?.name} transparentHeader>
      {/* CINEMATIC HERO ---------------------------------------------------- */}
      <section
        className="relative overflow-hidden bg-black text-white"
        style={{ minHeight: "92vh" }}
        data-testid="home-hero"
      >
        {HERO_VIDEO_SRC ? (
          <video autoPlay loop muted playsInline poster={HERO_STILL_A}
                 className="absolute inset-0 w-full h-full object-cover">
            <source src={HERO_VIDEO_SRC} type="video/mp4" />
          </video>
        ) : (
          <motion.img
            src={HERO_STILL_A}
            alt="Travel + Leisure Headquarters · 501 W. Church Street, Orlando"
            initial={{ scale: 1.02 }} animate={{ scale: 1.18 }}
            transition={{ duration: 38, ease: "linear" }}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 35%" }}
          />
        )}

        {/* Overlay gradients — strengthened for tagline legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-black/90 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent pointer-events-none" />

        {/* Black entrance veil */}
        <motion.div
          initial={{ opacity: 1 }} animate={{ opacity: 0 }}
          transition={{ duration: 1.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 bg-black z-30 pointer-events-none"
        />

        <div className="relative z-20 container-summit min-h-[92vh] flex flex-col justify-end pb-24 pt-32 sm:pb-28">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 2.0 }}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-white/70 mb-6">
            <span className="h-px w-10 bg-[#A39171]" />
            Travel <span className="text-[#A39171] -mx-1">+</span> Leisure
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 2.3, ease: [0.22, 1, 0.36, 1] }}
            className="serif text-5xl sm:text-7xl lg:text-8xl leading-[1.02] max-w-5xl tracking-tight">
            Strategic Supplier
            <br />
            Summit 2026
          </motion.h1>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 3.1 }}
            className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-sm sm:text-base text-white/90">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-[#A39171]" strokeWidth={1.4} />
              {event?.date || "Friday, September 11, 2026"}
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#A39171]" strokeWidth={1.4} />
              {event?.address || "501 W. Church Street, Orlando, FL"}
            </div>
          </motion.div>

          {/* Tagline — higher contrast: solid dark backing bar + brighter text */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 3.6 }}
            className="mt-8 max-w-2xl">
            <p className="serif italic text-lg sm:text-xl leading-relaxed text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
              &ldquo;{event?.tagline || "Building stronger supplier partnerships through Educational Sessions, networking, and Business Matchmaking."}&rdquo;
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 4.3 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Link to="/register" data-testid="home-reserve-cta"
              className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#A39171] hover:text-white transition-colors h-14 px-10 rounded-sm text-sm uppercase tracking-[0.22em] font-medium">
              Register for the Summit
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <button onClick={scrollToExplore} data-testid="home-explore-link"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white text-xs uppercase tracking-[0.28em] transition-colors">
              Explore the Summit
              <ArrowDown size={14} />
            </button>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1.0, delay: 5.0 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/50 text-[10px] uppercase tracking-[0.36em] flex flex-col items-center gap-2">
          <span>Scroll</span>
          <motion.span animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="h-6 w-px bg-white/40" />
        </motion.div>
      </section>

      <div ref={exploreRef} />

      {/* COUNTDOWN — supports the hero with event context on the right */}
      <section className="border-b border-[hsl(var(--line))] bg-white">
        <div className="container-summit py-8 md:py-10">
          <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
            <CountdownTo target={event?.event_start_iso} label={`Countdown to ${event?.date || "Sept 11"}`} compact />
            <div className="hidden md:flex items-center justify-end gap-8">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Date</div>
                <div className="serif text-lg mt-1">{event?.date || "Friday, September 11, 2026"}</div>
              </div>
              <div className="text-right border-l border-zinc-200 pl-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Venue</div>
                <div className="serif text-lg mt-1 leading-tight">
                  {event?.location || "Travel + Leisure HQ"}
                  <div className="text-xs text-zinc-500 font-sans mt-0.5">
                    {event?.address || "Orlando, FL"}
                  </div>
                </div>
              </div>
              <Link to="/register" data-testid="countdown-register-cta"
                className="hidden xl:inline-flex h-11 px-6 items-center justify-center text-xs uppercase tracking-[0.18em] font-semibold bg-black text-white hover:bg-[#A39171] hover:text-white transition-colors">
                Register
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* EXECUTIVE UPDATE ------------------------------------------------- */}
      {topUpdate && (
        <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white" data-testid="executive-update">
          <div className="container-summit py-4 flex items-start gap-4">
            <BellRing size={16} className="mt-0.5 text-[#A39171] shrink-0 animate-pulse" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-400">Executive Update</div>
              <div className="text-sm mt-1">
                <span className="font-medium">{topUpdate.title}.</span>{" "}
                {topUpdate.body && <span className="text-zinc-300">{topUpdate.body}</span>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* QUICK ACCESS — 5-col at desktop for a balanced row, 2-col on tablet, 1-col mobile */}
      <section className="border-b border-[hsl(var(--line))] bg-[#FAFAF7]">
        <div className="container-summit py-10 md:py-14">
          <FadeUp className="overline mb-6"><span className="accent-rule" />Quick Access</FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {QUICK_ACCESS.map((c) => (
              <Link key={c.to} to={c.to} data-testid={c.testId}
                className="group flex items-center justify-between gap-3 bg-white border border-black/10 hover:border-black hover:bg-black hover:text-white transition-all min-h-16 px-4 py-3 rounded-sm">
                <span className="flex items-center gap-3 text-xs uppercase tracking-[0.12em] font-medium leading-tight">
                  <c.icon size={16} strokeWidth={1.4} className="shrink-0" />
                  <span>{c.label}</span>
                </span>
                <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SUMMIT DASHBOARD ------------------------------------------------- */}
      <section className="border-b border-[hsl(var(--line))]" data-testid="summit-dashboard">
        <div className="container-summit py-10 md:py-16">
          <FadeUp className="overline mb-6"><span className="accent-rule" />Summit Dashboard</FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]">
            {dashboard.map((k, i) => (
              <FadeUp key={k.label} delay={i * 0.04} className="h-full">
                <div className="bg-white p-6 md:p-8 h-full flex flex-col justify-between min-h-[172px]" data-testid={k.testId}>
                  <div className="serif text-4xl sm:text-5xl md:text-6xl tabular-nums leading-none">
                    <CountUp value={k.value} />
                  </div>
                  <div className="overline mt-4">{k.label}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* EXECUTIVE QUOTE + WEATHER --------------------------------------- */}
      <section className="container-summit py-10 md:py-16" data-testid="executive-quote-section">
        <div className="grid lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          <FadeUp className="bg-white p-8 lg:col-span-2">
            <Quote size={20} className="text-[#A39171]" strokeWidth={1.4} />
            <p className="serif text-2xl sm:text-3xl mt-6 leading-snug">
              &ldquo;{event?.executive_quote || "The Strategic Supplier Summit is designed to strengthen procurement partnerships, accelerate supplier innovation, and create meaningful business opportunities."}&rdquo;
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="bg-white p-8 flex flex-col gap-4">
            <CloudSun size={20} className="text-[#A39171]" strokeWidth={1.4} />
            <div className="overline">Orlando, FL</div>
            <div className="serif text-5xl">
              {weather?.temperature_f ?? "—"}
              <span className="text-2xl">°F</span>
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Current conditions
            </div>
            <Link to="/ask" className="mt-auto btn-outline-black h-11 text-xs" data-testid="home-ask-ai-cta">
              <Sparkles size={14} /> Summit Concierge
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* TODAY'S HIGHLIGHTS section removed at owner request. Session details remain on /schedule. */}

      {/* FEATURED PRIME SUPPLIER + BUSINESS MATCHMAKING section removed at owner request.
          Directory + matchmaking are still reachable from Quick Access. */}

      {/* WHY ATTEND — six procurement categories ------------------------ */}
      <section className="border-t border-[hsl(var(--line))] bg-white" data-testid="why-attend">
        <div className="container-summit py-12 md:py-20">
          <FadeUp className="overline mb-6 md:mb-8"><span className="accent-rule" />Why Attend</FadeUp>
          <h2 className="serif text-3xl sm:text-4xl max-w-3xl">Where Travel + Leisure meets the suppliers building the next chapter.</h2>
          <p className="mt-4 text-zinc-600 max-w-2xl leading-relaxed">
            The Supplier Showcase is organized around six procurement categories that reflect Travel + Leisure&apos;s
            operational and strategic priorities.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
            {WHY_ATTEND.map((c, i) => (
              <FadeUp key={c.name} delay={i * 0.04}>
                <div className="bg-white p-8 h-full" data-testid={`why-attend-${c.name.toLowerCase().replace(/[^a-z]/g,"").slice(0,10)}`}>
                  <c.icon size={22} className="text-[#A39171]" strokeWidth={1.4} />
                  <div className="serif text-xl mt-5">{c.name}</div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SUMMIT PARTNERS -------------------------------------------------- */}
      <section className="border-t border-[hsl(var(--line))] bg-[#FAFAF7]" data-testid="summit-partners">
        <div className="container-summit py-12 md:py-16">
          <FadeUp className="overline mb-6 md:mb-8"><span className="accent-rule" />Summit Partners</FadeUp>
          <div className="grid md:grid-cols-2 gap-px bg-[hsl(var(--line))]">
            <FadeUp className="bg-white p-8">
              <div className="overline">Host</div>
              <div className="serif text-3xl mt-3">{host?.name || "Travel + Leisure"}</div>
              {host?.blurb && <p className="text-sm text-zinc-600 mt-3">{host.blurb}</p>}
            </FadeUp>
            <FadeUp delay={0.05} className="bg-white p-8">
              <div className="overline">Produced By</div>
              <div className="serif text-3xl mt-3">{producedBy?.name || "Display America"}</div>
              {producedBy?.blurb && <p className="text-sm text-zinc-600 mt-3">{producedBy.blurb}</p>}
            </FadeUp>
          </div>
          {/* Technology Partner + Supporting Organizations rows removed at owner request. */}
        </div>
      </section>

      {/* SUPPLIER JOURNEY section intentionally removed at owner request. Onboarding
          steps are still communicated within /onboarding and the Prime Supplier Guide. */}

      {/* PRIME SUPPLIER PARTICIPATION section intentionally removed at owner request.
          The $2,500 package is still communicated through /register, /onboarding,
          and the Prime Supplier Guide document. */}
    </Layout>
  );
}
